import { useRef, useState, useEffect, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { kickAudio, playNow, stopAllAudio } from "@/audio/piano";
import { playRhythm, setRhythmBpm, RhythmHandle } from "@/audio/rhythm";

/**
 * SAPShell — Sing & Play lesson component library.
 *
 * Components:
 *  SAPSection      — content card (eyebrow + title + body)
 *  SAPOomPahPlayer — interactive 2-lane OomPah pattern + chord loop
 *  SAPChordBox     — single clickable chord (plays the chord on click)
 *  SAPChordRow     — horizontal row of SAPChordBoxes with section label
 *  SAPCallout      — coloured callout box (practice tip / insight)
 *  SAPQuote        — Jason transcript quote
 *  SAPPracticeLadder — structured practice plan
 */

/* ── Content card ─────────────────────────────────────────────────────── */
export function SAPSection({
  eyebrow,
  title,
  children,
}: {
  eyebrow?: string;
  title?: string;
  children: ReactNode;
}) {
  const reduced = useReducedMotion();
  return (
    <motion.section
      initial={reduced ? false : { opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.12 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="mx-auto max-w-4xl px-4 py-4 lg:px-8"
    >
      <div className="rounded-2xl border border-purple-400/20 bg-zinc-900/70 p-5 shadow-[0_4px_24px_rgba(168,85,247,0.06)]">
        {eyebrow && (
          <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.28em] text-purple-400/80">
            {eyebrow}
          </div>
        )}
        {title && (
          <h2
            className="mb-3 text-2xl font-semibold text-purple-50 lg:text-3xl"
            style={{ fontFamily: '"Cormorant Garamond", "Spectral", Georgia, serif' }}
          >
            {title}
          </h2>
        )}
        <div className="space-y-3 text-[15.5px] leading-[1.78] text-zinc-300">{children}</div>
      </div>
    </motion.section>
  );
}

/* ── Single playable chord box ────────────────────────────────────────── */
export interface SAPChord {
  /** Label shown on the box — chord symbol like "D", "A7", "G/B" */
  label: string;
  /** Roman numeral function — "I", "IV", "V", "V7", "vi", etc. */
  rn?: string;
  /** Tone.js pitch strings to play on click */
  pitches?: string[];
  /** Functional colour category */
  fn?: "tonic" | "subdominant" | "dominant" | "submediant" | "borrowed" | "special";
  /** Flag for special or non-diatonic chords */
  special?: boolean;
}

const FN_STYLES: Record<NonNullable<SAPChord["fn"]>, { border: string; text: string; active: string }> = {
  tonic:       { border: "border-purple-400/50", text: "text-purple-200",  active: "bg-purple-500/15" },
  subdominant: { border: "border-sky-400/50",    text: "text-sky-200",    active: "bg-sky-500/15" },
  dominant:    { border: "border-amber-400/50",  text: "text-amber-200",  active: "bg-amber-500/15" },
  submediant:  { border: "border-emerald-400/50",text: "text-emerald-200",active: "bg-emerald-500/15" },
  borrowed:    { border: "border-rose-400/50",   text: "text-rose-200",   active: "bg-rose-500/15" },
  special:     { border: "border-fuchsia-400/50",text: "text-fuchsia-200",active: "bg-fuchsia-500/15" },
};
const DEFAULT_STYLE = { border: "border-zinc-600/50", text: "text-zinc-200", active: "bg-zinc-700/30" };

export function SAPChordBox({ chord }: { chord: SAPChord }) {
  const [playing, setPlaying] = useState(false);
  const style = chord.fn ? FN_STYLES[chord.fn] : DEFAULT_STYLE;

  const handleClick = async () => {
    if (!chord.pitches?.length) return;
    kickAudio();
    setPlaying(true);
    await playNow(chord.pitches, "2n", 0.8);
    setTimeout(() => setPlaying(false), 600);
  };

  return (
    <motion.button
      whileTap={{ scale: 0.93 }}
      onClick={handleClick}
      disabled={!chord.pitches?.length}
      className={`flex min-w-[52px] flex-col items-center rounded-xl border px-3 py-2.5 text-center transition-colors ${style.border} ${playing ? style.active : "bg-zinc-900/40 hover:bg-zinc-800/60"} disabled:cursor-default disabled:opacity-70`}
    >
      {chord.rn && (
        <div className={`text-[10px] font-bold uppercase tracking-wider ${style.text}`}>
          {chord.rn}
        </div>
      )}
      <div
        className={`text-xl font-semibold leading-tight ${style.text}`}
        style={{ fontFamily: '"Cormorant Garamond", "Spectral", Georgia, serif' }}
      >
        {chord.label}
      </div>
    </motion.button>
  );
}

/* ── Row of chord boxes with section label ────────────────────────────── */
export function SAPChordRow({
  label,
  chords,
  caption,
}: {
  label: string;
  chords: SAPChord[];
  caption?: string;
}) {
  return (
    <div className="my-4 rounded-xl border border-purple-400/20 bg-zinc-950/60 p-4">
      <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.22em] text-purple-400/80">
        {label} · click any chord to hear it
      </div>
      <div className="flex flex-wrap gap-2">
        {chords.map((c, i) => (
          <SAPChordBox key={i} chord={c} />
        ))}
      </div>
      {caption && (
        <p className="mt-3 text-[13px] italic text-zinc-400">{caption}</p>
      )}
    </div>
  );
}

/* ── OomPah pattern player ────────────────────────────────────────────── */
export interface OomPahChord {
  /** Display label above the cells */
  label: string;
  /** Number of bars this chord occupies */
  bars: number;
  /** RH chord pitches (played on off-beats) */
  rh: string[];
  /** LH root pitch (played on down-beats) */
  lhRoot: string;
  /** LH fifth pitch — alternates with root on beats 2 and 4 */
  lhFifth?: string;
}

/**
 * OomPahPlayer — two-lane grid (LH | RH) that shows the Oom-Pah pattern
 * and plays it in a loop using Tone.Transport.
 *
 * Visual: 8 cells per bar (eighth-note resolution). LH fires on cells
 * 0, 2, 4, 6 (beats 1-2-3-4). RH fires on cells 1, 3, 5, 7 (off-beats).
 */
export function OomPahPlayer({
  progression,
  bpm: initialBpm = 100,
  label = "OOM-PAH LOOP",
}: {
  progression: OomPahChord[];
  bpm?: number;
  label?: string;
}) {
  const [running, setRunning] = useState(false);
  const [step, setStep]   = useState(-1);
  const [bpm, setBpm]     = useState(initialBpm);
  const handleRef = useRef<RhythmHandle | null>(null);

  const CELLS_PER_BAR = 8; // 8 eighth-note cells per bar
  const totalBars = progression.reduce((s, c) => s + c.bars, 0);
  const totalSteps = totalBars * CELLS_PER_BAR;

  useEffect(() => {
    if (running) setRhythmBpm(bpm, 0.05);
  }, [bpm, running]);

  const stop = () => {
    handleRef.current?.stop();
    handleRef.current = null;
    setRunning(false);
    setStep(-1);
  };

  const play = async () => {
    kickAudio();
    stop();
    setRunning(true);

    const events: { beat: number; notes: string[]; durationSteps: number; velocity?: number }[] = [];

    let cellOffset = 0;
    for (const chord of progression) {
      for (let bar = 0; bar < chord.bars; bar++) {
        // LH on every beat (cells 0, 2, 4, 6)
        [0, 2, 4, 6].forEach((c, beatIdx) => {
          // Alternate root and fifth for the classic Oom-Pah bass motion
          const isOdd = beatIdx % 2 === 1;
          const bass = isOdd && chord.lhFifth ? chord.lhFifth : chord.lhRoot;
          events.push({
            beat: cellOffset + bar * CELLS_PER_BAR + c,
            notes: [bass],
            durationSteps: 2,
            velocity: c === 0 ? 0.85 : 0.7,
          });
        });
        // RH on off-beats (cells 1, 3, 5, 7)
        [1, 3, 5, 7].forEach((c) => {
          events.push({
            beat: cellOffset + bar * CELLS_PER_BAR + c,
            notes: chord.rh,
            durationSteps: 1,
            velocity: 0.72,
          });
        });
      }
      cellOffset += chord.bars * CELLS_PER_BAR;
    }

    handleRef.current = await playRhythm(events, {
      bpm,
      subdivide: 2,
      totalSteps,
      loop: true,
      click: false,
      beatsPerBar: 4,
      onStep: (s) => setStep(s),
    });
  };

  // Map step to which chord is currently active
  const activeChordIdx = (() => {
    if (step < 0) return -1;
    let acc = 0;
    for (let i = 0; i < progression.length; i++) {
      acc += progression[i].bars * CELLS_PER_BAR;
      if (step < acc) return i;
    }
    return -1;
  })();

  return (
    <div className="my-4 rounded-2xl border border-purple-400/25 bg-zinc-900/50 p-4">
      {/* Header */}
      <div className="mb-3 flex items-baseline justify-between">
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-purple-400">
          ♩ {label}
        </div>
        <div className="font-mono text-[11px] text-zinc-500">
          {progression.map((c) => c.label).join(" – ")}
        </div>
      </div>

      {/* Grid: chord label bar */}
      <ChordLabelBar progression={progression} activeIdx={activeChordIdx} cellsPerBar={CELLS_PER_BAR} />

      {/* LH lane */}
      <PatternLane
        label="LH Bass"
        labelColor="text-amber-300"
        totalSteps={totalSteps}
        hitCells={(i) => i % CELLS_PER_BAR % 2 === 0}
        activeStep={step}
        hitColor="bg-amber-500"
        dimColor="bg-zinc-800/40"
        beats={totalSteps}
      />

      {/* RH lane */}
      <PatternLane
        label="RH Chord"
        labelColor="text-purple-300"
        totalSteps={totalSteps}
        hitCells={(i) => i % CELLS_PER_BAR % 2 === 1}
        activeStep={step}
        hitColor="bg-purple-500"
        dimColor="bg-zinc-800/40"
        beats={totalSteps}
      />

      {/* Beat labels */}
      <BeatLabels totalSteps={totalSteps} cellsPerBar={CELLS_PER_BAR} />

      {/* Controls */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          onClick={running ? stop : play}
          className={`rounded-lg px-4 py-2 text-sm font-bold transition-colors ${
            running
              ? "border border-rose-400/60 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20"
              : "bg-purple-500 text-white hover:bg-purple-400"
          }`}
        >
          {running ? "■ Stop" : "▶ Play loop"}
        </button>
        <label className="flex items-center gap-2 text-[12px] text-zinc-300">
          <span className="font-mono uppercase tracking-wider text-zinc-400">BPM</span>
          <input
            type="range" min={60} max={140} step={1} value={bpm}
            onChange={(e) => setBpm(Number(e.target.value))}
            className="h-1 w-28 accent-purple-400"
          />
          <span className="w-8 font-mono text-[11px] text-zinc-200">{bpm}</span>
        </label>
      </div>
    </div>
  );
}

/* ── Internal helpers for OomPahPlayer ──────────────────────────────── */
function ChordLabelBar({
  progression,
  activeIdx,
  cellsPerBar,
}: {
  progression: OomPahChord[];
  activeIdx: number;
  cellsPerBar: number;
}) {
  const totalCells = progression.reduce((s, c) => s + c.bars * cellsPerBar, 0);
  return (
    <div
      className="mb-2 flex gap-0.5 overflow-hidden rounded-lg"
      style={{ display: "grid", gridTemplateColumns: progression.map((c) => `${c.bars}fr`).join(" ") }}
    >
      {progression.map((chord, i) => (
        <div
          key={i}
          className={`rounded py-1 text-center text-[12px] font-bold transition-colors ${
            activeIdx === i
              ? "bg-purple-500/30 text-purple-100"
              : "bg-zinc-800/40 text-zinc-400"
          }`}
        >
          {chord.label}
        </div>
      ))}
    </div>
  );
}

function PatternLane({
  label,
  labelColor,
  totalSteps,
  hitCells,
  activeStep,
  hitColor,
  dimColor,
}: {
  label: string;
  labelColor: string;
  totalSteps: number;
  hitCells: (i: number) => boolean;
  activeStep: number;
  hitColor: string;
  dimColor: string;
  beats: number;
}) {
  return (
    <div className="mb-1.5 flex items-center gap-2">
      <span className={`w-16 shrink-0 text-[10px] font-bold uppercase tracking-wider ${labelColor}`}>
        {label}
      </span>
      <div
        className="flex flex-1 gap-0.5"
        style={{ display: "grid", gridTemplateColumns: `repeat(${totalSteps}, minmax(0, 1fr))` }}
      >
        {Array.from({ length: totalSteps }, (_, i) => {
          const hit = hitCells(i);
          const isActive = i === activeStep;
          return (
            <motion.div
              key={i}
              animate={{ scale: isActive ? 1.2 : 1, boxShadow: isActive ? "0 0 0 1.5px #a855f7" : "0 0 0 0 transparent" }}
              transition={{ duration: 0.08 }}
              className={`h-8 rounded-sm ${hit ? hitColor : dimColor} ${isActive && hit ? "ring-1 ring-white/40" : ""}`}
            />
          );
        })}
      </div>
    </div>
  );
}

function BeatLabels({ totalSteps, cellsPerBar }: { totalSteps: number; cellsPerBar: number }) {
  const beatNames = ["1", "&", "2", "&", "3", "&", "4", "&"];
  return (
    <div
      className="mt-0.5 pl-[4.5rem]"
      style={{ display: "grid", gridTemplateColumns: `repeat(${totalSteps}, minmax(0, 1fr))` }}
    >
      {Array.from({ length: totalSteps }, (_, i) => (
        <span
          key={i}
          className={`text-center text-[9px] font-mono ${
            beatNames[i % cellsPerBar] === "1" ? "font-bold text-amber-400/80" : "text-zinc-600"
          }`}
        >
          {beatNames[i % cellsPerBar] === "1" || beatNames[i % cellsPerBar] === "&" ? beatNames[i % cellsPerBar] : ""}
        </span>
      ))}
    </div>
  );
}

/* ── Callout ──────────────────────────────────────────────────────────── */
export function SAPCallout({
  label = "Practice",
  variant = "tip",
  children,
}: {
  label?: string;
  variant?: "tip" | "insight" | "warning";
  children: ReactNode;
}) {
  const styles = {
    tip:     "border-l-4 border-purple-400/70 bg-purple-500/8",
    insight: "border-l-4 border-amber-400/70 bg-amber-500/8",
    warning: "border-l-4 border-rose-400/70 bg-rose-500/8",
  };
  const labelStyles = {
    tip:     "text-purple-300",
    insight: "text-amber-300",
    warning: "text-rose-300",
  };
  return (
    <aside className={`my-4 rounded-r-xl px-4 py-3 ${styles[variant]}`}>
      <div className={`mb-1 text-[10px] font-bold uppercase tracking-[0.22em] ${labelStyles[variant]}`}>
        {label}
      </div>
      <div className="text-[15px] leading-relaxed text-zinc-300">{children}</div>
    </aside>
  );
}

/* ── Jason quote ──────────────────────────────────────────────────────── */
export function SAPQuote({
  children,
  attribution = "Jason Zac · lesson video",
}: {
  children: ReactNode;
  attribution?: string;
}) {
  return (
    <figure className="my-5 rounded-2xl border-l-4 border-purple-500/70 bg-zinc-900/60 px-5 py-4">
      <blockquote
        className="text-[17px] leading-relaxed text-purple-50/95"
        style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}
      >
        {children}
      </blockquote>
      <figcaption className="mt-2 text-[11px] uppercase tracking-[0.18em] text-purple-400/70">
        — {attribution}
      </figcaption>
    </figure>
  );
}

/* ── Practice ladder ──────────────────────────────────────────────────── */
export interface SAPRung {
  title: string;
  minutes: number;
  detail: string;
  accent?: "purple" | "amber" | "cyan" | "rose" | "emerald";
}

export function SAPPracticeLadder({
  intro,
  rungs,
}: {
  intro?: ReactNode;
  rungs: SAPRung[];
}) {
  const totalMin = rungs.reduce((s, r) => s + r.minutes, 0);
  const accentMap = {
    purple:  { dot: "bg-purple-400",  border: "border-purple-400/30",  text: "text-purple-300" },
    amber:   { dot: "bg-amber-400",   border: "border-amber-400/30",   text: "text-amber-300"  },
    cyan:    { dot: "bg-cyan-400",    border: "border-cyan-400/30",    text: "text-cyan-300"   },
    rose:    { dot: "bg-rose-400",    border: "border-rose-400/30",    text: "text-rose-300"   },
    emerald: { dot: "bg-emerald-400", border: "border-emerald-400/30", text: "text-emerald-300"},
  };

  return (
    <div className="my-4 rounded-2xl border border-purple-400/20 bg-zinc-950/50 p-5">
      <div className="mb-3 flex items-baseline justify-between">
        <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-purple-400">
          ♟ Practice ladder
        </div>
        <div className="font-mono text-[11px] text-zinc-500">{totalMin} min total</div>
      </div>
      {intro && (
        <p className="mb-4 text-[14px] leading-relaxed text-zinc-400">{intro}</p>
      )}
      <ol className="space-y-3">
        {rungs.map((rung, i) => {
          const styles = accentMap[rung.accent ?? "purple"];
          return (
            <li key={i} className={`flex gap-3 rounded-xl border p-3 ${styles.border} bg-zinc-900/40`}>
              <div className={`mt-1 h-2 w-2 shrink-0 rounded-full ${styles.dot}`} />
              <div>
                <div className={`text-[13px] font-bold ${styles.text}`}>
                  {rung.title}
                  <span className="ml-2 font-mono text-[11px] font-normal text-zinc-500">
                    {rung.minutes} min
                  </span>
                </div>
                <div className="mt-0.5 text-[13px] text-zinc-400">{rung.detail}</div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/* ── 12-Bar Blues structure visual ────────────────────────────────────── */
export interface BluesBar {
  chord: string;
  fn: "I" | "IV" | "V" | "V7";
}

export function TwelveBarMap({
  bars,
  activeBar,
}: {
  bars: BluesBar[];
  activeBar: number;
}) {
  const fnColors: Record<string, { bg: string; text: string; ring: string }> = {
    I:  { bg: "bg-amber-500/20",  text: "text-amber-200",  ring: "ring-amber-400/60" },
    IV: { bg: "bg-sky-500/20",    text: "text-sky-200",    ring: "ring-sky-400/60"   },
    V:  { bg: "bg-rose-500/20",   text: "text-rose-200",   ring: "ring-rose-400/60"  },
    V7: { bg: "bg-rose-500/20",   text: "text-rose-200",   ring: "ring-rose-400/60"  },
  };

  return (
    <div
      className="my-4 grid gap-1.5 rounded-xl border border-zinc-700/40 bg-zinc-950/40 p-3"
      style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}
    >
      {bars.map((bar, i) => {
        const c = fnColors[bar.fn] ?? { bg: "bg-zinc-700/30", text: "text-zinc-300", ring: "ring-zinc-600/50" };
        const isActive = i === activeBar;
        return (
          <motion.div
            key={i}
            animate={{ scale: isActive ? 1.05 : 1 }}
            transition={{ duration: 0.1 }}
            className={`rounded-lg p-2 text-center ${c.bg} ${isActive ? `ring-2 ${c.ring}` : ""}`}
          >
            <div className={`text-[10px] font-mono uppercase tracking-wider ${c.text} opacity-70`}>
              Bar {i + 1}
            </div>
            <div className={`text-lg font-bold ${c.text}`} style={{ fontFamily: '"Cormorant Garamond", serif' }}>
              {bar.chord}
            </div>
            <div className={`text-[10px] font-mono ${c.text} opacity-60`}>{bar.fn}</div>
          </motion.div>
        );
      })}
    </div>
  );
}
